<!--
  AdminLicensing.svelte — the licensing backend registry.

  One row per registered application, because "which products have licensing, and
  does each one actually answer?" is a question the per-application edit page
  cannot answer — it shows one app at a time and has no way to tell a working
  backend URL from a plausible-looking typo.

  The writes go through the ordinary application update endpoint rather than a
  licensing-specific one: these are columns on `applications`, and a second write
  path for the same columns is a second thing to keep in step.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Badge, Button, Input, Select, Spinner, Toggle } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { EmptyState, LicenseAppIcon, RoutedLink } from '../../components';
	import { listApplications, updateApplication, probeLicenseBackend } from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import type { Application, LicenseBackendProbe } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	/** The adapters this build implements. */
	const ADAPTERS = [{ value: 'abstract-license-v1', name: 'Abstract Licensing Backend (v1)' }];

	/** The editable state of one application's licensing configuration. */
	interface Row {
		/** The application being configured. */
		app: Application;
		/** Whether licensing is switched on. */
		enabled: boolean;
		/** Backend API base, including the version prefix. */
		url: string;
		/** Which dialect the backend speaks. */
		adapter: string;
		/** Whether users may re-assign seats of their own licences. */
		seatManagement: boolean;
		/** Result of the last connection test, or `null` if it has not been run. */
		probe: LicenseBackendProbe | null;
		/** Whether a connection test is in flight. */
		testing: boolean;
		/** Whether a save is in flight. */
		saving: boolean;
	}

	/** One row per application. */
	let rows: Row[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Failure of the application list. */
	let error = $state('');
	/** Whether applications without licensing are shown. */
	let showAll = $state(false);

	/** Builds the editable row for one application. */
	function toRow(app: Application): Row {
		return {
			app,
			enabled: app.isLicensingEnabled,
			url: app.licenseBackendURL || '',
			adapter: app.licenseBackendAdapter || ADAPTERS[0].value,
			seatManagement: app.isSeatManagementEnabled,
			probe: null,
			testing: false,
			saving: false
		};
	}

	/** Load every application. Licensing is a property of an app, so the list is the registry. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch every application — the ones without licensing are what you turn it on for. */
		try {
			/* The registry is the application list, so it must not be truncated by the
			   server's default page size — this page exists to show every one. */
			rows = (await listApplications({ limit: 500 })).map(toRow);
			/* Default the filter to "configured only" unless nothing is configured yet,
			   in which case hiding everything would leave an empty page with no way in. */
			showAll = !rows.some((row) => row.enabled);
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load applications');
		} finally {
			loading = false;
		}
	}

	onMount(load);

	/** Rows currently on screen. */
	let visible = $derived(showAll ? rows : rows.filter((row) => row.enabled));

	/** How many applications have a licensing backend configured. */
	let configuredCount = $derived(rows.filter((row) => row.enabled).length);

	/** Whether a row differs from what is saved, which is what enables its save button. */
	function isDirty(row: Row): boolean {
		return (
			row.enabled !== row.app.isLicensingEnabled ||
			row.url.trim().replace(/\/+$/, '') !== (row.app.licenseBackendURL || '') ||
			row.adapter !== row.app.licenseBackendAdapter ||
			row.seatManagement !== row.app.isSeatManagementEnabled
		);
	}

	/** Saves one row's licensing configuration. */
	async function save(row: Row): Promise<void> {
		const url = row.url.trim().replace(/\/+$/, '');
		/* Enabling without a URL produces an application users would see in the picker
		   and that could only ever fail, so it is refused here rather than saved. */
		if (row.enabled && !url) {
			toastError(t2('A backend URL is required to enable licensing.'));
			return;
		}

		row.saving = true;
		/** Write the four licensing columns through the ordinary application update. */
		try {
			const updated = await updateApplication(row.app.id, {
				isLicensingEnabled: row.enabled,
				licenseBackendURL: url || null,
				licenseBackendAdapter: row.adapter,
				isSeatManagementEnabled: row.seatManagement
			});
			row.app = updated;
			row.url = url;
			toastSuccess(t2('Licensing saved for {{name}}.', { name: row.app.applicationName }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save licensing configuration'));
		} finally {
			row.saving = false;
		}
	}

	/** Runs a connection test against whatever URL is currently in the field. */
	async function test(row: Row): Promise<void> {
		row.testing = true;
		row.probe = null;
		/** Probe the backend and keep the result beside the row. */
		try {
			row.probe = await probeLicenseBackend(row.url, row.adapter);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to test the licensing backend'));
		} finally {
			row.testing = false;
		}
	}

	/**
	 * Whether a probe found the backend but bound to a different application.
	 *
	 * This is the failure the test exists to catch: a URL that answers perfectly
	 * while serving another product, which would otherwise show up only as users
	 * finding their licence list mysteriously empty.
	 */
	function isWrongProduct(row: Row): boolean {
		return (
			!!row.probe?.reachable &&
			!!row.probe.applicationUUID &&
			row.probe.applicationUUID !== row.app.applicationUUID
		);
	}
</script>

<div class="space-y-6">
	<div class="flex flex-wrap items-start justify-between gap-4">
		<div>
			<h3 class="text-xl font-semibold text-gray-900 dark:text-white">{$t('Licensing Backends')}</h3>
			<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
				{$t('Products whose licenses users can see from their Abstract ID account. Abstract ID stores no license data — it reads each backend live, as the signed-in user.')}
			</p>
		</div>
		{#if !loading && !error}
			<Toggle bind:checked={showAll}>{$t('Show all applications')}</Toggle>
		{/if}
	</div>

	{#if loading}
		<EmptyState variant="loading" />
	{:else if error}
		<EmptyState variant="error" description={error} />
	{:else if visible.length === 0}
		<EmptyState
			faIcon="key"
			title={$t('No licensing backends')}
			description={$t('Turn on "Show all applications" to point a product at its license server.')}
		/>
	{:else}
		<p class="text-sm text-gray-500 dark:text-gray-400">
			{$t('{{configured}} of {{total}} applications have a licensing backend.', {
				configured: configuredCount,
				total: rows.length
			})}
		</p>

		<div class="space-y-4">
			{#each visible as row (row.app.id)}
				<AbstractCard class="max-w-none">
					{#if row.app.isLicensingEnabled && !row.app.isActive}
						<div
							class="mb-4 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
						>
							<i class="fa-solid fa-triangle-exclamation me-2"></i>{$t('This application is inactive, so its licenses are hidden from users regardless of the settings below.')}
						</div>
					{/if}

					<div class="flex flex-wrap items-center gap-3">
						<div class="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2">
							<LicenseAppIcon
								iconImageName={row.app.iconImageName}
								icon={row.app.icon}
								name={row.app.applicationName}
							/>
						</div>
						<div class="min-w-0 flex-1">
							<RoutedLink to="/Applications/{row.app.id}">
								<span class="font-medium">{row.app.applicationName}</span>
							</RoutedLink>
							<p class="font-mono text-xs text-gray-400 dark:text-gray-500">{row.app.applicationUUID}</p>
						</div>
						<Toggle bind:checked={row.enabled}>{$t('Licensing')}</Toggle>
					</div>

					{#if row.enabled}
						<div class="mt-4 grid gap-3 md:grid-cols-[1fr_16rem]">
							<Input
								type="url"
								placeholder="https://license.example.com/api/v1"
								bind:value={row.url}
								aria-label={$t('Backend URL')}
							/>
							<Select bind:value={row.adapter} items={ADAPTERS} aria-label={$t('Adapter')} />
						</div>

						<div class="mt-3">
							<Toggle bind:checked={row.seatManagement}>{$t('Allow seat re-assignment')}</Toggle>
						</div>

						{#if row.probe}
							{#if isWrongProduct(row)}
								<div
									class="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
								>
									<p class="font-medium">
										<i class="fa-solid fa-triangle-exclamation me-2"></i>{$t('This backend serves a different application.')}
									</p>
									<p class="mt-1 font-mono text-xs">{row.probe.applicationUUID}</p>
									<p class="mt-1 text-xs">
										{$t('Users would see an empty license list. Check the URL points at this product’s license server.')}
									</p>
								</div>
							{:else if row.probe.reachable}
								<div
									class="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300"
								>
									<p class="font-medium">
										<i class="fa-solid fa-circle-check me-2"></i>{row.probe.productName || $t('Backend reachable')}
									</p>
									<!-- The count is what separates a broken integration from an account
									     that genuinely holds nothing: an empty licence page looks the same
									     either way, and this is the only place that says which it is. -->
									{#if row.probe.callerLicenseCount === null}
										<p class="mt-1 text-xs">
											{$t('The backend answered, but refused to list your licenses. Check that it shares the same JWT_KEY as Abstract ID.')}
										</p>
									{:else}
										<p class="mt-1 text-xs">
											{$t('It holds {{count}} license(s) for your account.', { count: row.probe.callerLicenseCount })}
										</p>
									{/if}
									<!-- Zero downloads beside licences that do exist is a specific
									     diagnosis, not a vague one: the builds have not been linked
									     to the entitlements those licences grant. -->
									{#if row.probe.callerDownloadCount !== null}
										<p class="mt-1 text-xs">
											{$t('It offers {{count}} download(s) for your account.', {
												count: row.probe.callerDownloadCount
											})}
											{#if row.probe.callerDownloadCount === 0 && (row.probe.callerLicenseCount ?? 0) > 0}
												{$t('Link builds to the entitlements your licenses grant to make them downloadable.')}
											{/if}
										</p>
									{/if}
									{#if !row.probe.seatDelegationConfigured}
										<p class="mt-1 text-xs">
											{$t('No sublicense entitlement is configured on the backend, so seats can never be re-assigned.')}
										</p>
									{/if}
								</div>
							{:else}
								<div
									class="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
								>
									<i class="fa-solid fa-circle-xmark me-2"></i>{row.probe.detail}
								</div>
							{/if}
						{/if}
					{/if}

					<div class="mt-4 flex flex-wrap items-center gap-2">
						{#if row.enabled}
							<Button size="sm" color="alternative" disabled={row.testing || !row.url.trim()} onclick={() => test(row)}>
								{#if row.testing}<Spinner size="4" class="me-2" />{/if}
								{$t('Test connection')}
							</Button>
						{/if}
						<Button size="sm" disabled={!isDirty(row) || row.saving} onclick={() => save(row)}>
							{#if row.saving}<Spinner size="4" class="me-2" />{/if}
							{$t('Save')}
						</Button>
						{#if isDirty(row)}
							<Badge color="yellow">{$t('Unsaved')}</Badge>
						{/if}
					</div>
				</AbstractCard>
			{/each}
		</div>
	{/if}
</div>
